import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import {
  Link,
  useParams,
  useNavigate,
  useLocation,
  matchPath,
} from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Badge,
  Spinner,
  Alert,
} from 'reactstrap';
import classnames from 'classnames';
import Swal from 'sweetalert2';
import useProcurementDetail from '../../../../shared/offer-domain/procurement/useProcurementDetail';
import useChangeEvaluationStatus from '../../hooks/useChangeEvaluationStatus';
import useClosingEvaluationStatus from '../../hooks/useClosingEvaluationStatus';
import useSubmittedBidder from '../../hooks/useSubmittedBidder';
import EvaluationOperational from './EvaluationOperational';
import Clarification from './Clarification';
import Improvement from './Improvement';
import ClarificationDetail from './Clarification/ClarificationDetail';
import { MAIN_TABS } from './constants/tabs';
import useProcurementDocuments from '../../hooks/useProcurementDocuments';
import { useQuantityDeliveries } from '../../../../Components/QuantityTable';
import {
  formatImprovementClosingDate,
  isImprovementClosingActive,
  resolveImprovementClosingDate,
} from '../../../../shared/offer-domain/improvement';
import { SubmitConfirmModal } from '../../../../shared/offer-domain/ui';
import {
  displayValue,
  flattenSubmittedBidderDeliveryDetails,
  getTenderEvaluationStatusConfig,
  getEvaluationOperationalLockReason,
  canAccessEvaluationOperationalData,
} from '../../utils/tenderEvaluationHelpers';
import {
  areAllBiddersClarificationDone,
  formatSubmittedDate,
} from '../../../../shared/offer-domain/clarification';
import {
  getVisibleProcurementClauses,
  useClarificationUnreadByMitra,
} from '../../../../shared/offer-domain/respond';

const CLARIFICATION_DETAIL_PATH =
  '/apps-tenderevaluation/dtl/:id/clarification/:offerId';

const DetailPage = ({
  initialMainTab = 'evaluation-operational',
  procurement,
  isLoading,
  refresh,
  unreadRefreshKey = 0,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState(initialMainTab);
  const [visitedMainTabs, setVisitedMainTabs] = useState({
    [initialMainTab]: true,
  });
  const [activeTab, setActiveTab] = useState('Offer');
  const [improvementClosingDate, setImprovementClosingDate] = useState(null);
  const [lockModalMode, setLockModalMode] = useState(null);
  const [lockGateMessage, setLockGateMessage] = useState('');
  const { documents: procurementDocuments, isLoading: isLoadingDocuments } =
    useProcurementDocuments(id, {
      enabled: Boolean(visitedMainTabs['evaluation-operational']),
      isActive: activeMainTab === 'evaluation-operational',
    });
  const { parcels, isLoading: isLoadingParcels } = useQuantityDeliveries(id, {
    enabled: Boolean(
      visitedMainTabs['evaluation-operational'] || visitedMainTabs.improvement
    ),
  });
  const { changeStatus, isLoading: isChangingStatus } =
    useChangeEvaluationStatus();
  const { closeEvaluation, isLoading: isClosingEvaluation } =
    useClosingEvaluationStatus();

  const evaluationLockReason = getEvaluationOperationalLockReason(procurement);
  const canAccessEvaluationData =
    canAccessEvaluationOperationalData(procurement);

  const { items: submittedBidders, refresh: refreshSubmittedBidders } =
    useSubmittedBidder(id, {
      enabled: Boolean(
        id &&
        (canAccessEvaluationData ||
          visitedMainTabs.clarification ||
          visitedMainTabs.improvement)
      ),
      isActive:
        activeMainTab === 'clarification' ||
        activeMainTab === 'improvement' ||
        (activeMainTab === 'evaluation-operational' && canAccessEvaluationData),
    });

  const clauseDetailIds = useMemo(
    () =>
      getVisibleProcurementClauses(procurement)
        .map(clause => clause.id)
        .filter(Boolean),
    [procurement]
  );
  const mitraIds = useMemo(
    () => (submittedBidders ?? []).map(bidder => bidder.id).filter(Boolean),
    [submittedBidders]
  );
  const { unreadByMitraId, hasAnyUnread: hasClarificationUnread } =
    useClarificationUnreadByMitra({
      procurementId: id,
      mitraIds,
      clauseDetailIds,
      isEvaluatorView: true,
      enabled: Boolean(id && mitraIds.length),
      refreshKey: unreadRefreshKey,
    });

  useEffect(() => () => toast.dismiss(), []);

  useEffect(() => {
    setVisitedMainTabs(prev =>
      prev[activeMainTab] ? prev : { ...prev, [activeMainTab]: true }
    );
  }, [activeMainTab]);

  useEffect(() => {
    if (
      (activeMainTab !== 'clarification' && activeMainTab !== 'improvement') ||
      !id
    ) {
      return;
    }
    refreshSubmittedBidders();
  }, [activeMainTab, id, refreshSubmittedBidders]);

  const statusConfig = getTenderEvaluationStatusConfig(
    procurement?.evaluationStatusWording
  );
  const categoryLabel =
    procurement?.procurementTypeHeaderLabel ||
    procurement?.productType?.product?.name ||
    'Product Hydro';

  const closingDateFromBidders = useMemo(() => {
    const rows = flattenSubmittedBidderDeliveryDetails(submittedBidders);
    return resolveImprovementClosingDate(rows);
  }, [submittedBidders]);

  const effectiveClosingDate =
    improvementClosingDate ?? closingDateFromBidders ?? null;
  const isImprovementDatePassed =
    !isImprovementClosingActive(effectiveClosingDate);
  const isClarificationDone = areAllBiddersClarificationDone(submittedBidders);

  const handleStartEvaluation = async () => {
    if (!procurement?.id) return;

    const result = await Swal.fire({
      title: 'Start Tender Evaluation?',
      text: 'Are you sure you want to start the tender evaluation process? this will allow you to access the bidding documents and start the evaluation process.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0062ff',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Start Tender Evaluation',
    });

    if (result.isConfirmed) {
      await changeStatus({
        procurementId: procurement.id,
        newStatus: 3,
        onSuccess: refresh,
      });
    }
  };

  const handleOpenLockModal = () => {
    const missing = [];
    if (!isClarificationDone) {
      missing.push(
        'All submitted bidders must have clarification status No Clarification or Closed/Closing.'
      );
    }
    if (!isImprovementDatePassed) {
      missing.push(
        effectiveClosingDate
          ? `Improvement is still active until ${formatImprovementClosingDate(effectiveClosingDate)}.`
          : 'Improvement period is still active.'
      );
    }

    if (missing.length) {
      setLockGateMessage(
        `Unable to lock evaluation yet:\n• ${missing.join('\n• ')}`
      );
      setLockModalMode('blocked');
      return;
    }

    setLockGateMessage(
      'Please make sure all clarification and improvement items are complete. Once locked, evaluation data can no longer be changed.'
    );
    setLockModalMode('confirm');
  };

  const handleConfirmLock = async () => {
    if (!procurement?.id) return;
    const success = await closeEvaluation({
      procurementId: procurement.id,
      onSuccess: refresh,
    });
    if (success) setLockModalMode(null);
  };

  const getEvaluationAlertConfig = () => {
    if (evaluationLockReason === 'awaiting') {
      return {
        color: 'danger',
        icon: 'bx bx-info-circle',
        title: 'Belum dapat Dimulai',
        desc: 'Dikarenakan proses tender belum memasuki waktu closing date',
      };
    }
    if (evaluationLockReason === null) {
      return {
        color: 'success',
        icon: 'ri-check-line',
        title: 'Evaluasi Sedang Berlangsung',
        desc: 'Proses evaluasi tender telah dimulai. Anda dapat mengakses dokumen penawaran untuk melanjutkan evaluasi.',
        action: (
          <Button
            color='success'
            className='text-nowrap fw-medium px-3'
            disabled={isClosingEvaluation}
            onClick={handleOpenLockModal}
          >
            {isClosingEvaluation ? (
              <>
                <Spinner size='sm' className='me-1' />
                Processing...
              </>
            ) : (
              'Lock/Saved Evaluation'
            )}
          </Button>
        ),
      };
    }
    return {
      color: 'primary',
      icon: 'ri-information-line',
      title: 'Mulai Proses Evaluasi',
      desc: 'Klik tombol di samping untuk memulai dan untuk mengakses dokumen penawaran',
      action: (
        <Button
          color='primary'
          className='text-nowrap fw-medium px-3'
          disabled={isChangingStatus}
          onClick={handleStartEvaluation}
        >
          {isChangingStatus ? (
            <>
              <Spinner size='sm' className='me-1' />
              Processing...
            </>
          ) : (
            'Start Tender Evaluation'
          )}
        </Button>
      ),
    };
  };

  const evaluationAlertConfig = getEvaluationAlertConfig();

  return (
    <div className='page-content'>
      <ToastContainer closeButton={false} />
      <Container fluid>
        <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
          <div className='email-content minimal-border'>
            <div className='p-4 pb-0'>
              <div className='border-bottom border-bottom-dashed'>
                <Row className='mt-n2 mb-1 mb-sm-0'>
                  <div className='col-sm order-3 order-sm-2'>
                    <div className='hstack gap-sm-1 align-items-center flex-wrap email-topbar-link'>
                      <h5 className='card-title mb-0 flex-grow-1'>
                        <Breadcrumb listClassName='p-3 py-2 bg-light'>
                          <BreadcrumbItem>
                            <Link to='/dashboard'>Home</Link>
                          </BreadcrumbItem>
                          <BreadcrumbItem>
                            <Link to='/apps-tenderevaluation/view'>
                              Tender Evaluation
                            </Link>
                          </BreadcrumbItem>
                          <BreadcrumbItem active>
                            Detail #
                            {displayValue(procurement?.procurementNumber)}
                          </BreadcrumbItem>
                        </Breadcrumb>
                      </h5>
                    </div>
                  </div>
                </Row>

                <Row className='mb-4'>
                  <Col xs={12}>
                    <Row className='align-items-center g-3'>
                      <Col xs={12} lg>
                        <div className='d-flex align-items-start gap-3'>
                          <Button
                            color='light'
                            className='border shadow-sm rounded-circle d-flex align-items-center justify-content-center p-0'
                            style={{ width: '2.5rem', height: '2.5rem' }}
                            onClick={() =>
                              navigate('/apps-tenderevaluation/view')
                            }
                          >
                            <i className='bx bx-arrow-back fs-5 text-dark'></i>
                          </Button>
                          <div className='flex-grow-1'>
                            {isLoading && !procurement ? (
                              <div className='d-flex align-items-center gap-2 py-2'>
                                <Spinner size='sm' color='primary' />
                                <span className='text-muted small'>
                                  Loading procurement...
                                </span>
                              </div>
                            ) : (
                              <>
                                <h4 className='mb-1 fw-bold text-dark'>
                                  {categoryLabel} —{' '}
                                  {displayValue(procurement?.titleProcurement)}
                                </h4>
                                <div className='d-flex flex-wrap align-items-center gap-2 text-muted small'>
                                  <i className='bx bx-hash'></i>
                                  <span>
                                    Procurement ID:{' '}
                                    {displayValue(
                                      procurement?.procurementNumber
                                    )}
                                  </span>

                                  <span className='d-none d-md-inline text-secondary opacity-25'>
                                    |
                                  </span>

                                  <i className='bx bx-calendar'></i>
                                  <span>
                                    {formatSubmittedDate(
                                      procurement?.submittedDateTenderOpening
                                    )}
                                  </span>

                                  <Badge
                                    className={`${statusConfig.className} fw-semibold px-3 py-1`}
                                    pill
                                  >
                                    {statusConfig.label}
                                  </Badge>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Alert
                      color={`${evaluationAlertConfig.color}`}
                      className='d-flex align-items-center justify-content-between gap-3 mt-2 mb-0'
                    >
                      <div className='d-flex align-items-start gap-2'>
                        <i
                          className={`${evaluationAlertConfig.icon} fs-18 mt-1`}
                        ></i>
                        <div>
                          <h5 className='alert-heading fs-14 fw-bold mb-1'>
                            {evaluationAlertConfig.title}
                          </h5>
                          <p className='mb-0 fs-13'>
                            {evaluationAlertConfig.desc}
                          </p>
                        </div>
                      </div>
                      {evaluationAlertConfig.action}
                    </Alert>
                    <hr className='mt-3 mb-0 border-secondary opacity-10' />
                  </Col>
                </Row>
              </div>

              <Card className='border-0 shadow-sm'>
                <CardBody className='p-0'>
                  <Nav tabs className='nav-tabs-custom nav-primary px-3 pt-3'>
                    {MAIN_TABS.map(tab => (
                      <NavItem key={tab.id}>
                        <NavLink
                          className={classnames('cursor-pointer', {
                            active: activeMainTab === tab.id,
                          })}
                          onClick={() => setActiveMainTab(tab.id)}
                        >
                          <i className={`${tab.icon} align-middle me-1`}></i>
                          <span
                            className={
                              tab.id === 'clarification' &&
                              hasClarificationUnread
                                ? 'fw-semibold text-info'
                                : 'fw-semibold'
                            }
                          >
                            {tab.label}
                          </span>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  <TabContent activeTab={activeMainTab} className='p-4'>
                    {visitedMainTabs['evaluation-operational'] && (
                      <TabPane tabId='evaluation-operational'>
                        <EvaluationOperational
                          activeTab={activeTab}
                          onChangeTab={setActiveTab}
                          procId={id}
                          procurement={procurement}
                          isLoadingProcurement={isLoading}
                          onRefreshProcurement={refresh}
                          parcels={parcels}
                          isLoadingParcels={isLoadingParcels}
                          procurementDocuments={procurementDocuments}
                          isLoadingDocuments={isLoadingDocuments}
                        />
                      </TabPane>
                    )}

                    {visitedMainTabs.clarification && (
                      <TabPane tabId='clarification'>
                        <Clarification
                          procurementId={id}
                          procurement={procurement}
                          isLoadingProcurement={isLoading}
                          unreadByMitraId={unreadByMitraId}
                          isActive={activeMainTab === 'clarification'}
                        />
                      </TabPane>
                    )}

                    {visitedMainTabs.improvement && (
                      <TabPane tabId='improvement'>
                        <Improvement
                          procurement={procurement}
                          procId={id}
                          parcels={parcels}
                          isLoadingParcels={isLoadingParcels}
                          isLoadingProcurement={isLoading}
                          closingDate={effectiveClosingDate}
                          onImprovementClosingDateChange={
                            setImprovementClosingDate
                          }
                          isActive={activeMainTab === 'improvement'}
                        />
                      </TabPane>
                    )}
                  </TabContent>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </Container>

      <SubmitConfirmModal
        isOpen={lockModalMode === 'blocked'}
        onClose={() => setLockModalMode(null)}
        title='Unable to Lock Evaluation'
        message={lockGateMessage}
        cancelLabel='Close'
        showConfirm={false}
      />
      <SubmitConfirmModal
        isOpen={lockModalMode === 'confirm'}
        onClose={() => setLockModalMode(null)}
        onConfirm={handleConfirmLock}
        title='Lock / Save Evaluation'
        message={lockGateMessage}
        confirmLabel='Lock Evaluation'
        cancelLabel='Back'
        isSubmitting={isClosingEvaluation}
      />
    </div>
  );
};

const TenderEvaluationDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const clarificationMatch = matchPath(
    { path: CLARIFICATION_DETAIL_PATH, end: true },
    location.pathname
  );
  const { procurement, isLoading, refresh } = useProcurementDetail(id);
  const wasClarificationDetailRef = useRef(Boolean(clarificationMatch));
  const [unreadRefreshKey, setUnreadRefreshKey] = useState(0);

  useEffect(() => {
    const isClarificationDetail = Boolean(clarificationMatch);
    if (wasClarificationDetailRef.current && !isClarificationDetail && id) {
      setUnreadRefreshKey(key => key + 1);
    }
    wasClarificationDetailRef.current = isClarificationDetail;
  }, [clarificationMatch, id]);

  return (
    <>
      <div className={clarificationMatch ? 'd-none' : undefined}>
        <DetailPage
          initialMainTab={
            clarificationMatch ? 'clarification' : 'evaluation-operational'
          }
          procurement={procurement}
          isLoading={isLoading}
          refresh={refresh}
          unreadRefreshKey={unreadRefreshKey}
        />
      </div>
      {clarificationMatch ? (
        <ClarificationDetail
          key={clarificationMatch.params.offerId}
          procurementId={clarificationMatch.params.id}
          offerId={clarificationMatch.params.offerId}
          procurement={procurement}
          isLoadingProcurement={isLoading}
        />
      ) : null}
    </>
  );
};

export default TenderEvaluationDetail;
